/**
 * HÌNH 6 – Video Conference tích hợp (Phiên tòa trực tuyến)
 * Video area 50% | AI Scribe Panel 50% (1/2 – 1/2).
 * Kịch bản mô phỏng: transcript chạy từng bước, hiển thị trực quan.
 */
import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material'
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  ScreenShare,
  CallEnd,
  Download,
  Edit,
  PauseCircleOutline,
  Description,
  Draw,
} from '@mui/icons-material'
import { useAppSelector } from '../../hooks/redux'
import { brandColors } from '../../theme'

type TranscriptLine = { speaker: string; text: string; time: string; role?: 'judge' | 'plaintiff' | 'defendant' | 'system' }

/** Kịch bản mô phỏng phiên tòa – tranh chấp thương mại phần mềm. */
const SCENARIO_SCRIPT: TranscriptLine[] = [
  { speaker: 'Thẩm phán', text: 'Hội đồng xét xử tuyên bố khai mạc phiên tòa. Vụ án KT-2026-012, tranh chấp hợp đồng mua bán phần mềm.', time: '09:00', role: 'judge' },
  { speaker: 'Thẩm phán', text: 'Mời Thư ký phiên tòa báo cáo về sự có mặt của các bên.', time: '09:01', role: 'judge' },
  { speaker: 'Thư ký', text: 'Thưa HĐXX, nguyên đơn và bị đơn đều có mặt. Viện kiểm sát đã cử Kiểm sát viên tham gia.', time: '09:01:30', role: 'system' },
  { speaker: 'Thẩm phán', text: 'Mời nguyên đơn trình bày yêu cầu khởi kiện và các tình tiết liên quan.', time: '09:02', role: 'judge' },
  { speaker: 'Nguyên đơn', text: 'Kính thưa Hội đồng xét xử. Công ty chúng tôi đã ký hợp đồng với bị đơn mua hệ thống phần mềm quản lý với giá trị 1,2 tỷ đồng. Bên bị đơn giao hàng trễ hạn, sản phẩm không đúng spec và không khắc phục. Chúng tôi yêu cầu hủy hợp đồng, hoàn trả toàn bộ tiền đã tạm ứng và bồi thường thiệt hại theo đơn.', time: '09:02:45', role: 'plaintiff' },
  { speaker: 'Thẩm phán', text: 'Mời bị đơn trình bày ý kiến.', time: '09:04', role: 'judge' },
  { speaker: 'Bị đơn', text: 'Thưa HĐXX. Chúng tôi đã giao phần mềm đúng tiến độ đã gia hạn với sự đồng ý của nguyên đơn. Các khiếu nại về lỗi kỹ thuật đã được chúng tôi gửi bản cập nhật khắc phục; nguyên đơn không hợp tác nghiệm thu. Chúng tôi phản đơn, yêu cầu buộc nguyên đơn thanh toán phần còn lại theo hợp đồng.', time: '09:04:35', role: 'defendant' },
  { speaker: 'Thẩm phán', text: 'HĐXX hỏi nguyên đơn: Bên nguyên đơn có nhận được bản cập nhật phần mềm mà bị đơn nói đến không?', time: '09:05', role: 'judge' },
  { speaker: 'Nguyên đơn', text: 'Dạ có nhận. Nhưng sau khi cập nhật vẫn còn lỗi nghiêm trọng, không đáp ứng yêu cầu nghiệm thu. Chúng tôi đã có biên bản ghi nhận và gửi bị đơn.', time: '09:05:40', role: 'plaintiff' },
  { speaker: 'Thẩm phán', text: 'HĐXX đã nghe ý kiến của các bên và Kiểm sát viên. HĐXX tạm hoãn phiên để nghị án. Phiên tòa tạm hoãn, mời các bên chờ công bố bản án.', time: '09:07', role: 'judge' },
]

const SPEAKER_COLORS: Record<string, string> = {
  judge: brandColors.primary,
  plaintiff: '#2e7d32',
  defendant: '#c62828',
  system: '#6d4c41',
}

const VIDEO_SOURCES = {
  judge: '/docs/u2769547523_Judge_Middle-aged_Vietnamese_male_judge_wearing_tham_phan.mp4',
  plaintiff: '/docs/u2769547523_Plaintiff_-_Middle-aged_Vietnamese_nguyen_don.mp4',
  defendant: '/docs/u2769547523_B_n_Defendant_Middle-aged_Vietnamese_man_wearing_bi_don.mp4',
}

export default function VideoConferencePage() {
  const user = useAppSelector((s) => s.auth.user)
  const transcriptEndRef = useRef<HTMLDivElement>(null)
  const [micOn, setMicOn] = useState(true)
  const [cameraOn, setCameraOn] = useState(true)
  const [scribePaused, setScribePaused] = useState(false)
  const [minutesCreated, setMinutesCreated] = useState(false)
  const [minutesSigned, setMinutesSigned] = useState(false)
  const [toast, setToast] = useState('')
  const [transcriptLines, setTranscriptLines] = useState<TranscriptLine[]>([])
  const [simulationIndex, setSimulationIndex] = useState(0)
  const isSecretary = user?.role === 'secretary'
  const simulationRunning = simulationIndex < SCENARIO_SCRIPT.length

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [transcriptLines])

  // Tự động play và replay: chạy từng dòng, hết thì đợi rồi reset và chạy lại.
  useEffect(() => {
    if (simulationIndex >= SCENARIO_SCRIPT.length) {
      const t = setTimeout(() => {
        setTranscriptLines([])
        setSimulationIndex(0)
      }, 3500)
      return () => clearTimeout(t)
    }
    const line = SCENARIO_SCRIPT[simulationIndex]
    const delay = Math.min(2200, 800 + line.text.length * 45)
    const t = setTimeout(() => {
      setTranscriptLines((prev) => [...prev, line])
      setSimulationIndex((i) => i + 1)
    }, delay)
    return () => clearTimeout(t)
  }, [simulationIndex])

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 120px)', display: 'flex', gap: 0, overflow: 'hidden', minHeight: 0 }}>
      <Box sx={{ flex: '0 0 50%', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1, gap: 1, overflow: 'hidden' }}>
          {/* Thẩm phán: 2/3 height */}
          <Box sx={{ flex: '0 0 66.666%', minHeight: 0, borderRadius: 2, border: 2, borderColor: 'primary.main', position: 'relative', overflow: 'hidden', bgcolor: '#000' }}>
            <video
              src={VIDEO_SOURCES.judge}
              autoPlay
              loop
              muted
              playsInline
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              aria-label="Thẩm phán"
            />
            <Chip label="Chủ trì · Thẩm phán" size="small" color="primary" sx={{ position: 'absolute', top: 8, left: 8, fontWeight: 600, zIndex: 1 }} />
          </Box>
          {/* Nguyên đơn + Bị đơn: 1/3 height (chia đôi) */}
          <Box sx={{ flex: '0 0 33.333%', minHeight: 0, display: 'flex', gap: 1, overflow: 'hidden' }}>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, borderRadius: 2, overflow: 'hidden', border: 2, borderColor: 'transparent', bgcolor: '#000', position: 'relative' }}>
              <video
                src={VIDEO_SOURCES.plaintiff}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  filter: 'brightness(1.35) contrast(1.15)',
                }}
                aria-label="Nguyên đơn"
              />
              <Chip label="Nguyên đơn" size="small" sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.75)', color: '#fff', fontWeight: 600, border: 'none' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, borderRadius: 2, overflow: 'hidden', border: 2, borderColor: 'transparent', bgcolor: '#000', position: 'relative' }}>
              <video
                src={VIDEO_SOURCES.defendant}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  filter: 'brightness(1.35) contrast(1.15)',
                }}
                aria-label="Bị đơn"
              />
              <Chip label="Bị đơn" size="small" sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.75)', color: '#fff', fontWeight: 600, border: 'none' }} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1, bgcolor: 'grey.100', borderTop: 1, borderColor: 'divider' }}>
          <IconButton color={micOn ? 'primary' : 'default'} onClick={() => setMicOn(!micOn)}>{micOn ? <Mic /> : <MicOff />}</IconButton>
          <IconButton color={cameraOn ? 'primary' : 'default'} onClick={() => setCameraOn(!cameraOn)}>{cameraOn ? <Videocam /> : <VideocamOff />}</IconButton>
          <IconButton><ScreenShare /></IconButton>
          <IconButton color="error" size="medium"><CallEnd /></IconButton>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', borderRadius: 0, borderLeft: 1, borderColor: 'divider', minWidth: 0 }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>AI Thư ký phiên tòa (AI Scribe)</Typography>
          {simulationRunning ? <Chip label="Đang ghi..." size="small" color="warning" /> : transcriptLines.length > 0 ? <Chip label="LIVE" size="small" color="error" /> : <Chip label="Chưa ghi" size="small" variant="outlined" />}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, bgcolor: 'grey.50' }}>
          {transcriptLines.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, textAlign: 'center', color: 'text.secondary' }}>
              <Description sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">Đang chuẩn bị transcript...</Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ py: 0 }}>
              {transcriptLines.map((item, i) => (
                <ListItem key={i} alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1, px: 1.5, mb: 0.5, bgcolor: 'background.paper', borderRadius: 1, borderLeft: 3, borderColor: item.role ? SPEAKER_COLORS[item.role] : 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: item.role ? SPEAKER_COLORS[item.role] : 'text.secondary' }}>
                      {item.speaker}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{item.time}</Typography>
                  </Box>
                  <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.5, whiteSpace: 'pre-wrap' } }} />
                </ListItem>
              ))}
              {simulationRunning && (
                <ListItem sx={{ py: 0.5, px: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">▌ Đang nhận dạng giọng nói...</Typography>
                </ListItem>
              )}
              <div ref={transcriptEndRef} />
            </List>
          )}
        </Box>
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" startIcon={<Download />}>Xuất biên bản</Button>
            <Button size="small" startIcon={<Edit />}>Chỉnh sửa</Button>
            <Button size="small" startIcon={<PauseCircleOutline />} onClick={() => setScribePaused(!scribePaused)} color={scribePaused ? 'primary' : 'inherit'}>
              {scribePaused ? 'Tiếp tục ghi' : 'Tạm dừng ghi'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Giai đoạn 5 – Sau phiên</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" startIcon={<Description />} onClick={() => { setMinutesCreated(true); setToast('Đã tạo dự thảo biên bản từ transcript. Chuyển Thư ký ký số.'); }} disabled={minutesCreated}>
              {minutesCreated ? 'Đã tạo biên bản' : 'Tạo biên bản từ transcript'}
            </Button>
            {isSecretary && (
              <Button size="small" variant="contained" color="primary" startIcon={<Draw />} onClick={() => { setMinutesSigned(true); setToast('Đã ký số. Biên bản phiên tòa đã được lưu chính thức.'); }} disabled={!minutesCreated || minutesSigned}>
                {minutesSigned ? 'Đã ký số' : 'Thư ký ký số'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  )
}
